import delay from 'gleam-shared/promises/delay'
import { post, patch, request, requestDeletion } from 'gleam-shared/network/http'
import { isCancelled, annotateError } from 'gleam-shared/network/isCancelled'
import { random, combine, isString } from 'gleam-shared/helpers/nodash'
import { ACCEPTED } from 'gleam-shared/network/statusCodes'

const FIRST_INTERVALS = [300, 700, 1_000, 2_000, 3_000, 5_000]
const LAST_INTERNAL_MIN = 5_000
const LAST_INTERNAL_MAX = 10_000

const TASK_LOCATION = 'task-location'
const TASK_SIGNATURE = 'task-signature'

const dedupedTasks = new Map()

export function clearDuplicateRegistry() {
  dedupedTasks.clear() // for specs
}

export function cancelTask(key) {
  dedupedTasks.get(key)?.abort()
  dedupedTasks.delete(key)
}

export function dedupedTask(url, options, key) {
  if (isString(options)) {
    key = options
    options = undefined
  } else if (!isString(key)) {
    key = url
  }

  if (!isString(url) || !isString(key)) {
    return Promise.reject(annotateError(new Error('dedupedTask expects the URL and key to be strings!')))
  }

  cancelTask(key)

  const controller = new AbortController()
  const signal = controller.signal
  dedupedTasks.set(key, controller)

  return scheduleTask(url, withSignal(options, signal)).then((response) => {
    dedupedTasks.delete(key)
    return response
  }, (error) => {
    const cancelled = isCancelled(error)
    if (!cancelled) {
      dedupedTasks.delete(key)
    }

    return Promise.reject(annotateError(error))
  })
}

export function scheduleTask(url, options) {
  const [signal, remainder] = withoutSignal(options)
  return request(url, withHttpResponse(remainder)).then((response) => {
    return handleTask(response, remainder, signal)
  })
}

export function scheduleCreation(url, body, options) {
  return post(url, body, withHttpResponse(options)).then((response) => {
    return handleTask(response, options)
  })
}

export function scheduleUpdate(url, body, options) {
  return patch(url, body, withHttpResponse(options)).then((response) => {
    return handleTask(response, options)
  })
}

export function scheduleDeletion(url, options) {
  return requestDeletion(url, withHttpResponse(options)).then((response) => {
    return handleTask(response, options)
  })
}

function withoutSignal(options) {
  const { signal, ...remainder } = (options ?? {})
  return [signal, remainder]
}

function withSignal(options, signal) {
  return combine(options, { signal })
}

function withHttpResponse(options) {
  return combine(options, { keepHttpResponse: true })
}

function handleTask(response, options, signal) {
  signal?.throwIfAborted()

  if (response.status === ACCEPTED && response.headers.has(TASK_LOCATION)) {
    return pollTask(
      response.headers.get(TASK_LOCATION),
      response.headers.get(TASK_SIGNATURE),
      options,
      signal
    )
  } else {
    return response.data
  }
}

function pollTask(location, signature, options, signal, intervalIndex = 0) {
  return delay(interval(intervalIndex)).then(() => {
    return pullTask(location, signature, options).then(({ value, status }) => {
      signal?.throwIfAborted()

      if (status === 100) {
        return value
      } else {
        return pollTask(location, signature, options, signal, intervalIndex + 1)
      }
    })
  })
}

function pullTask(location, signature, options) {
  if (signature) {
    return patch(location, null, withSignature(options, signature))
  } else {
    return request(location, options)
  }
}

function withSignature(options, signature) {
  return combine(options, {
    headers: combine(options?.headers, { [TASK_SIGNATURE]: signature })
  })
}

function interval(intervalIndex) {
  return FIRST_INTERVALS[intervalIndex] ??
    random(LAST_INTERNAL_MIN, LAST_INTERNAL_MAX)
}
